import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = ["open", "resolved", "all"];
const TARGET_LINK = (t, id) => ({
  listing: `/listings/${id}`,
  company: `/removal-companies/${id}`,
  brief: `/briefs/${id}`,
}[t]);

export default function AdminReports() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "open";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [action, setAction] = useState("dismiss");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/reports?status=${status}`).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/reports/${active.report_id}/resolve`, { action, note });
      toast.success("Report resolved");
      setActive(null); setNote(""); setAction("dismiss");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="overline text-safety">Reports</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">User reports</h1>
      <p className="mt-1 text-sm text-muted-foreground">Every report requires a manual decision.</p>

      <div className="mt-6 flex border border-border w-fit">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setParams({ status: t })}
            data-testid={`admin-reports-tab-${t}`}
            className={`px-5 h-10 text-xs overline transition-colors duration-200 ${status === t ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-border bg-white">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No {status} reports.</div>
        ) : items.map((r, i) => (
          <div key={r.report_id} data-testid={`admin-report-row-${r.report_id}`} className={`p-5 ${i > 0 ? "border-t border-border" : ""}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge className="rounded-none bg-forest text-white overline">{r.target_type}</Badge>
              <Badge className={`rounded-none overline ${r.status === "open" ? "bg-safety text-white" : "bg-sage-dark text-forest"}`}>{r.status}</Badge>
              <div className="overline text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</div>
              {r.resolution && <div className="overline text-[10px] text-forest">→ {r.resolution}</div>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-start">
              <div>
                <div className="text-xs text-muted-foreground">Target: <span className="font-mono">{r.target_id}</span></div>
                {r.target_snippet && <div className="mt-1 text-sm">{r.target_snippet}</div>}
                <div className="mt-2 text-sm leading-relaxed"><span className="overline text-muted-foreground">Reason:</span> {r.reason}</div>
                <div className="mt-1 text-xs text-muted-foreground">Reported by {r.reporter_name}</div>
                {r.admin_note && <div className="mt-2 text-xs text-forest"><span className="overline">Admin note:</span> {r.admin_note}</div>}
              </div>
              <div className="flex items-center gap-2">
                {TARGET_LINK(r.target_type, r.target_id) && (
                  <Button asChild variant="outline" size="sm" className="rounded-none">
                    <a href={TARGET_LINK(r.target_type, r.target_id)} target="_blank" rel="noreferrer">View target</a>
                  </Button>
                )}
                {r.status === "open" && (
                  <Button
                    onClick={() => setActive(r)}
                    size="sm"
                    data-testid={`admin-report-resolve-${r.report_id}`}
                    className="rounded-none bg-forest hover:bg-forest-800 text-white"
                  >
                    Resolve →
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Resolve report</DialogTitle>
          </DialogHeader>
          {active && (
            <>
              <div className="text-sm">
                <div className="overline text-muted-foreground">Reason</div>
                <div className="mt-1">{active.reason}</div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Decision</div>
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger className="rounded-none" data-testid="admin-report-action"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="dismiss">Dismiss (no action)</SelectItem>
                    <SelectItem value="warn_user">Warn user (note only)</SelectItem>
                    <SelectItem value="remove_content">Remove content</SelectItem>
                    <SelectItem value="suspend_user">Suspend responsible user</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Admin note (optional)</div>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-report-note" className="rounded-none" />
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)} className="rounded-none">Cancel</Button>
            <Button
              onClick={resolve}
              disabled={submitting}
              data-testid="admin-report-resolve-submit"
              className="rounded-none bg-safety hover:bg-safety-hover text-white"
            >
              {submitting ? "Resolving…" : "Confirm decision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
